import { useState, useMemo, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useProducts } from "@/hooks/useProducts";
import { useProductsStock } from "@/hooks/useProductsStock";
import { useCart } from "@/hooks/useCart";
import { ProductDetail } from "@/mocks/productDetails";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import WhatsAppFloat from "@/pages/home/components/WhatsAppFloat";
import AnnouncementBar from "@/pages/home/components/AnnouncementBar";
import SearchBar from "./components/SearchBar";
import FilterSidebar from "./components/FilterSidebar";
import ProductCard from "./components/ProductCard";
import { useThemeColor, hexAlpha } from "@/hooks/useThemeColor";

const CATEGORIES = ["الكل", "PS5", "PS4", "PC", "Controllers", "Headsets"];

const CATEGORY_SEARCH_MAP: Record<string, string> = {
  "PS5": "بلايستيشن 5 ps5 playstation",
  "PS4": "بلايستيشن 4 ps4 playstation",
  "PC": "كمبيوتر ألعاب pc gaming",
  "Controllers": "أيادي تحكم controllers يد تحكم جويستيك",
  "Headsets": "سماعات headsets سماعة",
};

function sortProducts(products: ProductDetail[], sortBy: string): ProductDetail[] {
  const arr = [...products];
  switch (sortBy) {
    case "price-asc": return arr.sort((a, b) => a.price - b.price);
    case "price-desc": return arr.sort((a, b) => b.price - a.price);
    case "rating": return arr.sort((a, b) => b.rating - a.rating);
    case "reviews": return arr.sort((a, b) => b.reviews - a.reviews);
    default: return arr;
  }
}

const PRODUCTS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "جميع المنتجات — متجر الإبداع",
  "description": "تصفّح جميع منتجات الألعاب: PS5، PS4، كمبيوتر ألعاب، أيادي تحكم وسماعات بأفضل الأسعار",
  "url": `${import.meta.env.VITE_SITE_URL ?? "https://example.com"}/products`,
  "inLanguage": "ar-SA",
  "isPartOf": {
    "@type": "WebSite",
    "name": "متجر الإبداع AL Ebdaa",
    "url": import.meta.env.VITE_SITE_URL ?? "https://example.com"
  }
};

/* ══════════════════════════════════════════
   SPECS MODAL (reused from ProductsSection)
══════════════════════════════════════════ */
function SpecsModal({
  product,
  liveStock,
  stockLoading,
  onClose,
}: {
  product: ProductDetail;
  liveStock: number | undefined;
  stockLoading: boolean;
  onClose: () => void;
}) {
  const tc = useThemeColor();
  const { addItem, items } = useCart();
  const [activeTab, setActiveTab] = useState<"highlights" | "specs" | "box">("highlights");
  const [imgIdx, setImgIdx] = useState(0);
  const [added, setAdded] = useState(false);

  const stock = liveStock ?? product.stock ?? 0;
  const isOutOfStock = !stockLoading && stock === 0;
  const isInCart = items.some((i) => i.id === product.id);
  const disc = product.originalPrice > product.price
    ? Math.round((1 - product.price / product.originalPrice) * 100) : 0;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", handler); document.body.style.overflow = ""; };
  }, [onClose]);

  const handleAdd = () => {
    addItem({ id: product.id, nameAr: product.nameAr, price: product.price, image: product.images?.[0] ?? "", category: product.category });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const categoryLabels: Record<string, string> = {
    "PS5": "PS5", "PS4": "PS4", "PC": "كمبيوتر ألعاب", "Controllers": "أيادي التحكم", "Headsets": "سماعات",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl flex flex-col"
        style={{ background: "#111", border: `1px solid ${hexAlpha(tc.primary, 0.2)}` }} dir="rtl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold px-3 py-1 rounded-full"
              style={{ background: hexAlpha(tc.primary, 0.15), color: tc.primary, fontFamily: "'Cairo', sans-serif" }}>
              {categoryLabels[product.category] ?? product.category}
            </span>
            {product.badge && (
              <span className="text-xs font-bold px-3 py-1 rounded-full text-black"
                style={{ background: tc.primary, fontFamily: "'Cairo', sans-serif" }}>{product.badge}</span>
            )}
          </div>
          <button onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-white/10 text-gray-400 hover:text-white hover:border-white/30 transition-all cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">

            {/* Left: Image */}
            <div className="bg-[#0d0d0d] p-6 flex flex-col gap-4">
              <div className="relative w-full h-64 md:h-72 rounded-xl overflow-hidden bg-[#0a0a0a]">
                {product.images?.[imgIdx] ? (
                  <img src={product.images[imgIdx]} alt={product.nameAr} className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-image-line text-gray-700 text-5xl"></i>
                  </div>
                )}
                {disc > 0 && (
                  <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full" style={{ fontFamily: "'Cairo', sans-serif" }}>-{disc}%</span>
                )}
                {isOutOfStock && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-xl">
                    <span className="bg-red-500 text-white text-sm font-bold px-4 py-2 rounded-full" style={{ fontFamily: "'Cairo', sans-serif" }}>نفد المخزون</span>
                  </div>
                )}
              </div>

              {product.images?.length > 1 && (
                <div className="flex gap-2 flex-wrap">
                  {product.images.map((img, i) => (
                    <button key={i} onClick={() => setImgIdx(i)}
                      className="w-14 h-14 rounded-lg overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0"
                      style={{ borderColor: imgIdx === i ? tc.primary : "rgba(255,255,255,0.1)" }}>
                      <img src={img} alt="" className="w-full h-full object-cover object-top" />
                    </button>
                  ))}
                </div>
              )}

              {/* Price + CTA */}
              <div>
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-3xl font-bold" style={{
                    fontFamily: "'Cairo', sans-serif",
                    background: isOutOfStock ? undefined : `linear-gradient(to left, ${tc.primaryLight}, ${tc.primary})`,
                    WebkitBackgroundClip: isOutOfStock ? undefined : "text",
                    WebkitTextFillColor: isOutOfStock ? undefined : "transparent",
                    backgroundClip: isOutOfStock ? undefined : "text",
                    color: isOutOfStock ? "#6b7280" : undefined,
                  }}>
                    {product.price.toLocaleString()} ر.س
                  </span>
                  {product.originalPrice > product.price && (
                    <span className="text-gray-600 text-base line-through" style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {product.originalPrice.toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {isOutOfStock ? (
                    <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                      <i className="ri-close-circle-fill text-red-400"></i>
                      <span className="text-red-400 text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>غير متوفر حالياً</span>
                    </div>
                  ) : (
                    <button onClick={handleAdd}
                      className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
                      style={{
                        fontFamily: "'Cairo', sans-serif",
                        background: added || isInCart ? "#10b981" : `linear-gradient(to right, ${tc.primary}, ${tc.primaryDark})`,
                        color: "white",
                      }}>
                      <i className={`text-base ${added || isInCart ? "ri-check-line" : "ri-shopping-cart-line"}`}></i>
                      {added || isInCart ? "تمت الإضافة للسلة!" : "أضف للسلة"}
                    </button>
                  )}
                  <Link to={`/product/${product.id}`} onClick={onClose}
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap border border-white/10 text-gray-400"
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(tc.primary, 0.4); (e.currentTarget as HTMLElement).style.color = tc.primary; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.1)"; (e.currentTarget as HTMLElement).style.color = "#9ca3af"; }}>
                    <i className="ri-external-link-line text-sm"></i>
                    صفحة المنتج الكاملة
                  </Link>
                </div>
              </div>
            </div>

            {/* Right: Details */}
            <div className="p-6 flex flex-col gap-5">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2 leading-snug" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {product.nameAr}
                </h2>
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm" style={{ color: i < product.rating ? tc.primary : "#374151" }}></i>
                    ))}
                  </div>
                  <span className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>({product.reviews} تقييم)</span>
                </div>
                {product.shortDesc && (
                  <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>{product.shortDesc}</p>
                )}
              </div>

              {/* Tabs */}
              <div className="flex gap-1 bg-[#0a0a0a] rounded-xl p-1">
                {[
                  { key: "highlights", label: "المميزات", icon: "ri-star-line" },
                  { key: "specs", label: "المواصفات", icon: "ri-cpu-line" },
                  { key: "box", label: "الصندوق", icon: "ri-box-3-line" },
                ].map((t) => (
                  <button key={t.key} onClick={() => setActiveTab(t.key as typeof activeTab)}
                    className="flex-1 flex items-center justify-center gap-1.5 text-xs font-bold py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                    style={{
                      fontFamily: "'Cairo', sans-serif",
                      background: activeTab === t.key ? hexAlpha(tc.primary, 0.15) : "transparent",
                      color: activeTab === t.key ? tc.primary : "#6b7280",
                      border: activeTab === t.key ? `1px solid ${hexAlpha(tc.primary, 0.25)}` : "1px solid transparent",
                    }}>
                    <i className={`${t.icon} text-[11px]`}></i>{t.label}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto max-h-64 md:max-h-72 pr-1"
                style={{ scrollbarWidth: "thin", scrollbarColor: `${hexAlpha(tc.primary, 0.3)} transparent` }}>
                {activeTab === "highlights" && (
                  <ul className="space-y-2">
                    {(product.highlights ?? []).map((h, i) => (
                      <li key={i} className="flex items-start gap-3 bg-[#0d0d0d] rounded-xl px-4 py-3 border border-white/5">
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-double-line text-sm" style={{ color: tc.primary }}></i>
                        </span>
                        <span className="text-gray-300 text-sm leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>{h}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {activeTab === "specs" && (
                  <div className="flex flex-col gap-1">
                    {(product.specs ?? []).map((spec, i) => (
                      <div key={i} className={`flex items-start gap-4 px-4 py-3 rounded-xl ${i % 2 === 0 ? "bg-[#0d0d0d]" : "bg-transparent"}`}>
                        <span className="text-gray-500 text-xs w-32 flex-shrink-0 pt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>{spec.label}</span>
                        <span className="text-gray-200 text-xs font-medium flex-1" style={{ fontFamily: "'Cairo', sans-serif" }}>{spec.value}</span>
                      </div>
                    ))}
                  </div>
                )}
                {activeTab === "box" && (
                  <div className="grid grid-cols-1 gap-2">
                    {(product.inBox ?? []).map((item, i) => (
                      <div key={i} className="flex items-center gap-3 bg-[#0d0d0d] border border-white/5 rounded-xl px-4 py-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-archive-line text-sm" style={{ color: tc.primary }}></i>
                        </div>
                        <span className="text-gray-300 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { products, loading } = useProducts();
  const productIds = useMemo(() => products.map((p) => p.id), [products]);
  const { getStock, loading: stockLoading } = useProductsStock(productIds);

  const [specsProduct, setSpecsProduct] = useState<ProductDetail | null>(null);
  const [barVisible, setBarVisible] = useState(true);
  const handleBarVisibility = useCallback((v: boolean) => setBarVisible(v), []);

  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  const [category, setCategory] = useState(searchParams.get("cat") ?? "الكل");
  const [sortBy, setSortBy] = useState("default");
  const [minRating, setMinRating] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-products";
    script.textContent = JSON.stringify(PRODUCTS_SCHEMA);
    document.head.appendChild(script);
    document.title = "جميع المنتجات — متجر الإبداع AL Ebdaa";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "تصفّح جميع منتجات الألعاب: PS5، PS4، كمبيوتر ألعاب، أيادي تحكم وسماعات بأفضل الأسعار في المملكة العربية السعودية.");
    return () => {
      const el = document.getElementById("schema-products");
      if (el) el.remove();
    };
  }, []);

  const maxPrice = useMemo(() => {
    if (!products.length) return 10000;
    return Math.ceil(Math.max(...products.map((p) => p.price)) / 100) * 100;
  }, [products]);

  const [priceRange, setPriceRange] = useState<[number, number]>([0, maxPrice]);

  useEffect(() => {
    setPriceRange([0, maxPrice]);
  }, [maxPrice]);

  // Sync URL params
  useEffect(() => {
    const params: Record<string, string> = {};
    if (search) params.q = search;
    if (category !== "الكل") params.cat = category;
    setSearchParams(params, { replace: true });
  }, [search, category, setSearchParams]);

  const filtered = useMemo(() => {
    let result = products;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((p) => {
        const catExtra = CATEGORY_SEARCH_MAP[p.category] ?? "";
        return (
          p.nameAr.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          catExtra.toLowerCase().includes(q) ||
          (p.shortDesc ?? "").toLowerCase().includes(q) ||
          (p.longDesc ?? "").toLowerCase().includes(q) ||
          (p.badge ?? "").toLowerCase().includes(q) ||
          p.highlights?.some((h) => h.toLowerCase().includes(q))
        );
      });
    }

    if (category !== "الكل") {
      result = result.filter((p) => p.category === category);
    }

    result = result.filter(
      (p) => p.price >= priceRange[0] && p.price <= priceRange[1]
    );

    if (minRating > 0) {
      result = result.filter((p) => p.rating >= minRating);
    }

    return sortProducts(result, sortBy);
  }, [products, search, category, priceRange, minRating, sortBy]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (category !== "الكل") count++;
    if (priceRange[0] > 0 || priceRange[1] < maxPrice) count++;
    if (minRating > 0) count++;
    if (sortBy !== "default") count++;
    return count;
  }, [category, priceRange, maxPrice, minRating, sortBy]);

  const handleReset = () => {
    setCategory("الكل");
    setPriceRange([0, maxPrice]);
    setMinRating(0);
    setSortBy("default");
    setSearch("");
  };

  return (
    <div dir="rtl" className="bg-[#0a0a0a] min-h-screen">
      <AnnouncementBar onVisibilityChange={handleBarVisibility} />
      <Navbar barVisible={barVisible} />

      <main className="pt-24 pb-20 px-4">
        <div className="max-w-7xl mx-auto">

          {/* Page Header */}
          <div className="mb-8">
            <p className="text-amber-400 text-xs tracking-widest uppercase mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              تسوّق الآن
            </p>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              جميع المنتجات
            </h1>
            <p className="text-gray-500 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {loading ? "جاري التحميل..." : `${products.length} منتج متاح`}
            </p>
          </div>

          {/* Search + Controls Bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="flex-1">
              <SearchBar value={search} onChange={setSearch} products={products} />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`lg:hidden flex items-center gap-2 px-4 py-3 rounded-xl text-sm border transition-all cursor-pointer whitespace-nowrap ${
                  showFilters || activeFiltersCount > 0
                    ? "bg-amber-400/10 border-amber-400/30 text-amber-400"
                    : "bg-[#141414] border-white/10 text-gray-400"
                }`}
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-filter-3-line"></i>
                فلترة
                {activeFiltersCount > 0 && (
                  <span className="bg-amber-400 text-black text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
              <div className="flex items-center bg-[#141414] border border-white/10 rounded-xl overflow-hidden">
                <button onClick={() => setViewMode("grid")}
                  className={`w-10 h-10 flex items-center justify-center transition-all cursor-pointer ${viewMode === "grid" ? "bg-amber-400 text-black" : "text-gray-500 hover:text-white"}`}>
                  <i className="ri-grid-line text-base"></i>
                </button>
                <button onClick={() => setViewMode("list")}
                  className={`w-10 h-10 flex items-center justify-center transition-all cursor-pointer ${viewMode === "list" ? "bg-amber-400 text-black" : "text-gray-500 hover:text-white"}`}>
                  <i className="ri-list-check text-base"></i>
                </button>
              </div>
            </div>
          </div>

          {(search || activeFiltersCount > 0) && !loading && (
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <span className="text-gray-400 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                {filtered.length === 0 ? "لا توجد نتائج" : `${filtered.length} نتيجة`}
                {search && <span className="text-white"> لـ &quot;{search}&quot;</span>}
              </span>
              {search && (
                <button onClick={() => setSearch("")}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-amber-400 transition-colors cursor-pointer"
                  style={{ fontFamily: "'Cairo', sans-serif" }}>
                  <i className="ri-close-circle-line"></i>مسح البحث
                </button>
              )}
              {activeFiltersCount > 0 && (
                <button onClick={handleReset}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-amber-400 transition-colors cursor-pointer"
                  style={{ fontFamily: "'Cairo', sans-serif" }}>
                  <i className="ri-refresh-line"></i>إعادة تعيين الفلاتر
                </button>
              )}
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-6">
            <div className={`lg:block ${showFilters ? "block" : "hidden"}`}>
              <FilterSidebar
                categories={CATEGORIES}
                selectedCategory={category}
                onCategoryChange={setCategory}
                priceRange={priceRange}
                onPriceChange={setPriceRange}
                maxPrice={maxPrice}
                minRating={minRating}
                onRatingChange={setMinRating}
                sortBy={sortBy}
                onSortChange={setSortBy}
                onReset={handleReset}
                activeFiltersCount={activeFiltersCount}
              />
            </div>

            <div className="flex-1 min-w-0">
              {loading ? (
                <div className={`grid gap-5 ${viewMode === "grid" ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3" : "grid-cols-1"}`}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="bg-[#141414] border border-white/5 rounded-xl overflow-hidden animate-pulse">
                      <div className="w-full h-52 bg-[#1e1e1e]"></div>
                      <div className="p-5 space-y-3">
                        <div className="h-3 bg-[#1e1e1e] rounded w-1/3"></div>
                        <div className="h-5 bg-[#1e1e1e] rounded w-2/3"></div>
                        <div className="h-4 bg-[#1e1e1e] rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <div className="w-20 h-20 flex items-center justify-center bg-[#141414] rounded-full mb-5">
                    <i className="ri-search-line text-3xl text-gray-600"></i>
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>لا توجد نتائج</h3>
                  <p className="text-gray-500 text-sm mb-5" style={{ fontFamily: "'Cairo', sans-serif" }}>جرّب تغيير كلمة البحث أو الفلاتر</p>
                  <button onClick={handleReset}
                    className="px-5 py-2.5 bg-amber-400 hover:bg-amber-500 text-black text-sm font-bold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: "'Cairo', sans-serif" }}>
                    عرض جميع المنتجات
                  </button>
                </div>
              ) : (
                <div className={`grid gap-5 ${viewMode === "grid" ? "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3" : "grid-cols-1"}`}>
                  {filtered.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      viewMode={viewMode}
                      liveStock={stockLoading ? undefined : getStock(product.id)}
                      onShowSpecs={() => setSpecsProduct(product)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppFloat />

      {/* Specs Modal */}
      {specsProduct && (
        <SpecsModal
          product={specsProduct}
          liveStock={stockLoading ? undefined : getStock(specsProduct.id)}
          stockLoading={stockLoading}
          onClose={() => setSpecsProduct(null)}
        />
      )}
    </div>
  );
}
